import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    Output,
    EventEmitter,
    Inject,
    PLATFORM_ID
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, filter, switchMap, take, delay } from 'rxjs/operators';
import { AuthenticationService } from '@app/_services/authentication.service';
import { SharedService } from '@app/_services/shared.service';
import { CurrencyService } from '@app/_services/currency.service';
import { CurrencyRateService } from '@app/_services/currency-rate.service';
import { UserService } from '@app/_services/user.service';
import { CartService } from '@app/_services/cart.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHelper } from '@app/_helpers/translate-helper';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import * as _ from 'lodash';

@Component({
    selector: 'app-country-select',
    standalone: true,
    imports: [CommonModule, TranslateModule, ConfirmationDialogComponent],
    templateUrl: './country-select.component.html',
    styleUrls: ['./country-select.component.scss']
})
export class CountrySelectComponent implements OnInit, OnDestroy {
    @Input() variant: 'header' | 'menu' = 'header';
    @Input() styleClass: string = '';
    @Output() selectChange = new EventEmitter<any>();

    countries: any[] = [];
    selectedCountry: any = null;
    isOpen = false;
    showConfirmDialog = false;
    pendingCountry: any = null;
    private destroy$ = new Subject<void>();

    // SAFEGUARD: Prevent multiple simultaneous country/currency updates
    // This flag protects against race conditions from:
    // - Rapid clicking on country selector
    // - Multiple currency/country changes in quick succession
    // - Concurrent initialization and user interaction
    private isSavingCurrency = false;

    constructor(
        private authService: AuthenticationService,
        private sharedService: SharedService,
        private currencyService: CurrencyService,
        private currencyRateService: CurrencyRateService,
        private userService: UserService,
        private cartService: CartService,
        public translate: TranslateService,
        private toastr: ToastrService,
        public translateHelper: TranslateHelper,
        @Inject(PLATFORM_ID) private platformId: any
    ) {}

    get isArabic(): boolean {
        return this.translate.currentLang === 'ar';
    }

    ngOnInit(): void {
        // Subscribe to selected country changes
        this.sharedService.currentCountry$
            .pipe(takeUntil(this.destroy$))
            .subscribe(country => {
                this.selectedCountry = country;
            });

        // Load countries list for the dropdown
        // Note: Country initialization is now handled by app.component.ts
        // which properly uses the backend session's selectedCountryId
        this.authService.currentUser
            .pipe(
                // Wait until user data is loaded (null for guest, object for logged in)
                filter(() => this.authService.currentUserValue !== undefined),
                // Add small delay to ensure auth service has finished setting up
                delay(100),
                // Then load countries
                switchMap(() => this.authService.getCountryCodes()),
                // Only take first emission
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe((response: any) => {
                this.countries = response?.data || [];
                // Countries list is now available for dropdown
                // Actual country selection already initialized by app.component.ts
            });
    }

    toggleDropdown(): void {
        this.isOpen = !this.isOpen;
    }

    closeDropdown(): void {
        this.isOpen = false;
    }

    selectCountry(country: any): void {
        // Check if country is actually different
        if (this.selectedCountry?.code === country.code) {
            this.closeDropdown();
            return;
        }

        // Close dropdown immediately for better UX
        this.closeDropdown();

        // Check if cart has items
        const cartHasItems = this.cartService.quantity > 0;

        if (cartHasItems) {
            // Store the pending country change and show confirmation dialog
            this.pendingCountry = country;
            this.showConfirmDialog = true;
        } else {
            // No items in cart, proceed directly
            this.proceedWithCountryChange(country);
        }
    }

    /**
     * User confirmed the country change - clear cart and proceed
     */
    onConfirmCountryChange(): void {
        this.showConfirmDialog = false;

        if (!this.pendingCountry) {
            return;
        }

        const countryToSet = this.pendingCountry;
        this.pendingCountry = null;

        // Clear the cart via backend
        this.cartService.clearCart().subscribe({
            next: () => {
                // Cart cleared successfully, proceed with country change
                this.proceedWithCountryChange(countryToSet);
            },
            error: error => {
                console.error('[CountrySelect] ✗ Failed to clear cart:', error);
                // Even if clearing fails, proceed with country change
                // The cart will be cleared on reload anyway
                this.proceedWithCountryChange(countryToSet);
            }
        });
    }

    /**
     * User cancelled the country change
     */
    onCancelCountryChange(): void {
        this.showConfirmDialog = false;
        this.pendingCountry = null;
        // Reset safeguard flag since we're not proceeding
        this.isSavingCurrency = false;
    }

    /**
     * Proceed with the country change (after confirmation or if cart is empty)
     * 
     * CRITICAL FLOW:
     * 1. Save country to backend (session + DB if logged in)
     * 2. Save country to localStorage (best-effort, may fail in private mode)
     * 3. Update shared service state (in-memory)
     * 4. Set currency based on country
     * 5. Reload page to ensure full consistency
     */
    private proceedWithCountryChange(country: any): void {
        // SAFEGUARD: Prevent multiple simultaneous country changes
        if (this.isSavingCurrency) {
            return;
        }

        // Edge case: No country _id
        if (!country._id) {
            this.showWarningAndReload(
                this.translate.currentLang === 'ar'
                    ? 'بيانات البلد غير صالحة. سيتم إعادة تحميل الصفحة.'
                    : 'Invalid country data. Page will reload.'
            );
            return;
        }

        // Mark as saving to prevent duplicate calls
        this.isSavingCurrency = true;

        // STEP 1: Save country to backend (session + DB)
        // This is the source of truth - must succeed before proceeding
        this.userService.setCountry(country._id).subscribe({
            next: (response) => {
                if (response.code === 200) {
                    
                    // STEP 2: Save to localStorage (best-effort)
                    const savedToStorage = this.sharedService.setSelectedCountry(country);
                    if (!savedToStorage) {
                        console.warn(
                            '[CountrySelect] Running in private/incognito mode - relying on session'
                        );
                    }

                    // STEP 3: Emit change event
                    this.selectChange.emit(country);

                    // STEP 4: Set currency for the new country
                    // This will also trigger a reload after currency is saved
                    this.setCurrencyForCountry(country);
                } else {
                    this.showWarningAndReload(
                        this.translate.currentLang === 'ar'
                            ? 'فشل حفظ البلد. سيتم إعادة تحميل الصفحة.'
                            : 'Failed to save country. Page will reload.'
                    );
                }
            },
            error: (error) => {
                
                // Even on error, try to update localStorage and currency
                // This provides best-effort consistency
                this.sharedService.setSelectedCountry(country);
                this.selectChange.emit(country);
                
                this.showWarningAndReload(
                    this.translate.currentLang === 'ar'
                        ? 'فشل حفظ البلد. سيتم إعادة تحميل الصفحة.'
                        : 'Failed to save country. Page will reload.'
                );
            }
        });
    }

    /**
     * Automatically set the currency based on the selected country
     * This deprecates the need for a separate currency selector
     * 
     * CRITICAL: This is called AFTER country is saved to backend
     * The currency save + reload ensures full data consistency
     */
    private setCurrencyForCountry(country: any): void {
        // Note: isSavingCurrency flag already set by proceedWithCountryChange

        // Edge case 1: No currency data on country
        if (!country.currency) {
            this.showWarningAndReload(
                this.translate.currentLang === 'ar'
                    ? 'لا توجد معلومات عملة لهذا البلد. سيتم إعادة تحميل الصفحة.'
                    : 'No currency data for this country. Page will reload.'
            );
            return;
        }

        const selectedCurrency = country.currency;

        // RACE CONDITION FIX: Check if currency is already correctly set
        // This prevents unnecessary backend calls and infinite loops
        const currentCurrency = this.currencyService.options?.code;
        if (currentCurrency === selectedCurrency.symbol) {
            // Still reload to ensure full consistency with new country
            setTimeout(() => this.reloadPage(), 300);
            return;
        }

        // Edge case 2: Currency missing required fields
        if (!selectedCurrency._id || !selectedCurrency.symbol) {
            console.warn(
                '[CountrySelect] ⚠ Invalid currency data:',
                selectedCurrency
            );
            this.showWarningAndReload(
                this.translate.currentLang === 'ar'
                    ? 'بيانات عملة غير صالحة. سيتم إعادة تحميل الصفحة.'
                    : 'Invalid currency data. Page will reload.'
            );
            return;
        }



        // 1. Update currency service options (immediate UI feedback)
        this.currencyService.options = {
            code: selectedCurrency.symbol,
            symbol: selectedCurrency.symbol
        };

        // 2. Broadcast currency change to other components
        this.currencyService.currenciesSubject$.next({
            currencies: [selectedCurrency], // Just pass the selected currency
            selectedCurrencyId: selectedCurrency._id
        });

        // 3. Save currency preference to backend
        // No need to check login status - backend handles both logged-in and guest
        this.saveCurrencyPreferenceAndReload(selectedCurrency);
    }

    /**
     * Save currency preference to backend and reload page
     * 
     * CRITICAL CONSISTENCY FLOW:
     * 1. Country saved to backend (already done by proceedWithCountryChange)
     * 2. Currency saved to backend (this function) 
     * 3. Page reload ensures fresh data from backend session
     * 
     * Backend handles both logged-in users (DB + session) and guests (session only)
     * Works in all modes including private/incognito (session cookies still work)
     * 
     * DATA FLOW:
     * - Logged in: backend saves to both DB and session
     * - Guest: backend saves to session only
     * - On reload: backend loads from session (or DB if logged in)
     * - This ensures country + currency + rates are all synchronized
     */
    private saveCurrencyPreferenceAndReload(currency: any): void {
        // Save preference to backend (updates session AND user preference)
        this.userService.setCurrency(currency._id).subscribe({
            next: response => {
                if (response.code === 200) {
                    // CRITICAL: Delay ensures backend session is fully committed
                    // This prevents race conditions on reload
                    setTimeout(() => {
                        this.reloadPage();
                    }, 300);
                } else {
                    // Reload anyway - backend might have saved it
                    this.reloadPage();
                }
            },
            error: error => {
                console.error('[CountrySelect] ✗ Failed to save currency:', error);
                // Reload anyway - country change already applied
                // User can manually select currency after reload if needed
                this.showWarningAndReload(
                    this.translate.currentLang === 'ar'
                        ? 'فشل حفظ العملة. سيتم إعادة تحميل الصفحة.'
                        : 'Failed to save currency. Page will reload.'
                );
            }
        });
    }

    /**
     * Show a warning toast and reload after a delay
     */
    private showWarningAndReload(message: string): void {
        this.toastr.warning(message, '', {
            timeOut: 2000,
            closeButton: true
        });
        setTimeout(() => {
            // Reset safeguard flag before reload (in case reload is cancelled)
            this.isSavingCurrency = false;
            this.reloadPage();
        }, 2000);
    }

    /**
     * Reload the page with visual feedback
     */
    private reloadPage(): void {
        if (isPlatformBrowser(this.platformId)) {
            // Add visual indicator that reload is happening
            const body = document.querySelector('body');
            if (body) {
                body.style.opacity = '0.6';
                body.style.pointerEvents = 'none';
            }

            setTimeout(() => {
                window.location.reload();
            }, 150); // Small delay to ensure all saves are complete
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
